import { Box, Slider, styled } from "@mui/material";
import moment from "moment";
import React, { useMemo } from "react";
import Loader from "../../Loader/Loader";
import { deviceApi } from "../../../features/api/device/device.service";
import { useSelector } from "react-redux";
import { selectedCustomersIdsSelector } from "../../../features/authentication/auth-slice";
import { Device } from "../../../types";
import { trimAssetName } from "../../../Common/EndPoints";

type IpsPacket = {
  deviceId: string;
  deviceType: string;
  label: string;
  lastActivityTime: number;
  value: string;
};

const CustomSlider = styled(Slider)(({ theme }) => ({
  height: 4,
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "transparent",
  },
  "& .MuiSlider-thumb": {
    height: 20,
    width: 20,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#bfbfbf",
    height: 4,
    width: 2,
    "&.MuiSlider-markActive": {
      opacity: 1,
      backgroundColor: "currentColor",
    },
  },
  "& .MuiSlider-markLabel": {
    transform: "translateY(-10px)",
    color: theme.palette.text.secondary,
    "&.MuiSlider-markLabelActive": {
      color: theme.palette.text.primary,
    },
  },
  [`&.MuiSlider-sizeSmall`]: {
    padding: "3px 0",
  },
}));

type Config = {
  cmin: number;
  cmax: number;
  wmin: number;
  wmax: number;
  min: number;
  max: number;
  description: string;
};

const DEFAULT_CONFIG = {
  cmin: 15,
  cmax: 40,
  wmin: 18,
  wmax: 32,
  min: 22,
  max: 28,
  description: "",
};

const IpsChart: React.FC<{ data: IpsPacket; config: Config }> = ({ data, config }) => {
  const { value } = data;
  const numerical_value = Number(value) / 1000;
  const max_value = Math.max(config.cmax, numerical_value);
  const MIN_VALUE = Math.min(config.cmin, numerical_value);

  const getPercentage = (value: number) => ((value - MIN_VALUE) / (max_value - MIN_VALUE)) * 100;

  function getMarks() {
    return [
      { value: MIN_VALUE, label: `${MIN_VALUE}V` },
      { value: config.wmin, label: `${config.wmin}V` },
      { value: config.min, label: `${config.min}V` },
      { value: config.max, label: `${config.max}V` },
      { value: config.wmax, label: `${config.wmax}V` },
      { value: max_value, label: "" },
    ];
  }
  const marks = getMarks();

  // Custom styles for different ranges
  const getRangeColor = (value: any) => {
    switch (value) {
      case MIN_VALUE:
        return "red";
      case config.wmin:
        return "orange";
      case config.min:
        return "green";
      case config.max:
        return "orange";
      case config.wmax:
        return "red";
    }
  };

  return (
    <div className="ips--grid-container">
      <div>{moment(data.lastActivityTime).format("DD-MM-YYYY HH:mm:ss")}</div>
      <div>{data.label}</div>
      <Box sx={{ width: "100%", padding: 0, height: "100px" }}>
        <CustomSlider
          min={MIN_VALUE}
          size="small"
          max={max_value}
          value={numerical_value}
          disabled
          marks={marks}
          valueLabelDisplay="on"
          components={{
            // Custom rail component to show different colors
            Rail: ({ ownerState, ...props }) => (
              <Box {...props}>
                {marks.slice(0, -1).map((mark, index) => (
                  <Box
                    key={mark.value}
                    sx={{
                      position: "absolute",
                      height: "100%",
                      backgroundColor: getRangeColor(mark.value),
                      left: `${getPercentage(mark.value)}%`,
                      width: `${getPercentage(marks[index + 1].value) - getPercentage(mark.value)}%`,
                    }}
                  />
                ))}
              </Box>
            ),
          }}
        />
      </Box>
      <div>{config.description}</div>
    </div>
  );
};

function getConfigMap(data: Device[]) {
  const map: Record<string, Config> = {};
  for (const device of data) {
    const name = trimAssetName(device.name);
    const config = device.additionaldetail;
    map[name] = {
      cmax: config?.cmax ? Number(config?.cmax) : DEFAULT_CONFIG.cmax,
      cmin: config?.cmin ? Number(config?.cmin) : DEFAULT_CONFIG.cmin,
      description: config?.description ? config?.description : DEFAULT_CONFIG.description,
      max: config?.max ? Number(config?.max) : DEFAULT_CONFIG.max,
      min: config?.min ? Number(config?.min) : DEFAULT_CONFIG.min,
      wmax: config?.wmax ? Number(config?.wmax) : DEFAULT_CONFIG.wmax,
      wmin: config?.wmin ? Number(config?.wmin) : DEFAULT_CONFIG.wmin,
    };
  }
  return map;
}

interface Props {
  data: IpsPacket[];
  loading?: boolean;
  typeid: string;
}

const IpsTelemetry: React.FC<Props> = ({ data, loading = false, typeid }) => {
  const customerIdList = useSelector(selectedCustomersIdsSelector) as string[];
  const { data: device_list = [] } = deviceApi.useGetDevicesQuery({
    params: { deviceTypeIds: typeid, customerIds: customerIdList.toString() },
  });

  const config_map = useMemo(() => getConfigMap(device_list), [device_list]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="ips--grid-container">
            <div>Timestamp</div>
            <div>Device</div>
            <div></div>
            <div>Description</div>
          </div>
          {data.map((_data) => (
            <IpsChart data={_data} config={_data.label in config_map ? config_map[_data.label] : DEFAULT_CONFIG} />
          ))}
        </>
      )}
    </div>
  );
};

export default IpsTelemetry;
